import { useState } from 'react';
import { isPasswordConfirmationRequiredError } from '../errors';

/**
 * Every 2FA management endpoint carries Fortify's `password.confirm` middleware, so any of
 * these actions can 423 mid-flow (e.g. the confirmation window expired since the page loaded).
 * Centralises "open the confirm-password modal, then retry" so each call site doesn't
 * reimplement it.
 */
export type PasswordConfirmationGuard = ReturnType<typeof usePasswordConfirmationGuard>;

export function usePasswordConfirmationGuard() {
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  return {
    isModalOpen: pendingAction !== null,
    closeModal: () => setPendingAction(null),
    /** Run `action` now if the password is already confirmed, otherwise defer it until the modal confirms. */
    run: (isConfirmed: boolean, action: () => void) => {
      if (isConfirmed) {
        action();
      } else {
        setPendingAction(() => action);
      }
    },
    /** Call from a mutation's onError. Returns true if it captured the error (a 423), false otherwise. */
    retryIfRequired: (err: unknown, action: () => void): boolean => {
      if (!isPasswordConfirmationRequiredError(err)) {
        return false;
      }
      setPendingAction(() => action);
      return true;
    },
    /** Call once the confirm-password modal reports success. */
    resolve: () => {
      // Read the action out and clear the state before running it: a `setState` updater must
      // stay pure, or StrictMode's double-invoke fires the deferred request twice.
      setPendingAction(null);
      pendingAction?.();
    },
  };
}
